<script lang="ts">
import { RouteNames } from "@/utils/constants.ts";

export function getRouteOnboardBusinessProfile() {
  return {
    name: RouteNames.ONBOARDING_BUSINESS_PROFILE
  };
}
</script>

<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import CardLayout from "@/components/layout/CardLayout.vue";
import { TextInput } from "@/components/form/index.ts";
import InputSelect from "@/components/form/InputSelect.vue";
import BasicFormTitle from "@/components/basic/BasicFormTitle.vue";
import { getRouteOnboardAccountType } from "@/views/Onboarding/OnboardingAccountType.vue";
import { getRouteOnboardBusinessQuestionnaire } from "@/views/Onboarding/OnboardingBusinessQuestionnaire.vue";
import Countries from "@/libs/core/countries.ts";
import { Icon } from "@iconify/vue";
import { PersonService } from "@/services";
import { usePersonStore } from "@/store";
import { error } from "@/utils/toast.ts";

const router = useRouter();
const personService = new PersonService();
const personStore = usePersonStore();
const isLoading = ref(false);

// Prepare countries list
const countries: any = {};
for (const i in Countries) {
  const country = Countries[i];
  countries[country.code3] = country;
}

// Company Details
const companyName = ref("");
const registrationCountry = ref("LVA");
const registrationNumber = ref("");
const companyLegalForm = ref("");
const registrationDate = ref("");

// Tax Information
const taxCountry = ref("LVA");
const taxNumber = ref("");

// Contact Information
const websiteAddress = ref("");
const phoneNumber = ref("");

// Legal Address Information
const legalAddressLine = ref("");
const legalCity = ref("");
const legalCountry = ref("LVA");
const legalPostcode = ref("");

// Actual Address Information
const actualAddressApplicable = ref(false);
const actualAddressLine = ref("");
const actualCity = ref("");
const actualCountry = ref("LVA");
const actualPostcode = ref("");

// Business Information
const naceCode = ref("");
const requiresLicense = ref(false);
const businessActivitiesDescription = ref("");

async function submitForm() {
  isLoading.value = true;
  try {
    const profileData = {
      companyName: companyName.value,
      registrationCountry: registrationCountry.value,
      registrationNumber: registrationNumber.value,
      companyLegalForm: companyLegalForm.value,
      registrationDate: registrationDate.value,
      taxCountry: taxCountry.value,
      taxNumber: taxNumber.value,
      websiteAddress: websiteAddress.value,
      phoneNumber: phoneNumber.value,
      legalAddressLine: legalAddressLine.value,
      legalCity: legalCity.value,
      legalCountry: legalCountry.value,
      legalPostcode: legalPostcode.value,
      actualAddressApplicable: actualAddressApplicable.value,
      actualAddressLine: actualAddressLine.value,
      actualCity: actualCity.value,
      actualCountry: actualCountry.value,
      actualPostcode: actualPostcode.value,
      naceCode: naceCode.value,
      requiresLicense: requiresLicense.value,
      businessActivitiesDescription: businessActivitiesDescription.value
    };

    // Save business profile data
    console.log("Business profile data:", profileData);
    await personService.createBusinessProfile("business-profile", profileData);

    // Refresh person state to get updated data
    await personStore.getState();

    // Navigate to business questionnaire page after submission
    await router.push(getRouteOnboardBusinessQuestionnaire());
  } catch (e) {
    console.error("Error submitting business profile:", e);
    error("Failed to save business profile");
  } finally {
    isLoading.value = false;
  }
}

const goBack = () => {
  router.push(getRouteOnboardAccountType());
};

const isFormValid = () => {
  return !!(companyName.value &&
         registrationNumber.value &&
         companyLegalForm.value &&
         registrationDate.value &&
         taxNumber.value &&
         websiteAddress.value &&
         phoneNumber.value &&
         legalAddressLine.value &&
         legalCity.value &&
         legalPostcode.value &&
         naceCode.value &&
         businessActivitiesDescription.value);
};
</script>

<template>
  <CardLayout
    title="Business Account"
    subtitle="Application for opening and maintaining a business current account"
    heading="Company"
    :max-steps="4"
    :current-step="2"
    :next="submitForm"
    :back="goBack"
    :isAllowedNext="isFormValid()"
  >
    <div class="flex flex-col w-full gap-6 md:gap-8">
      <!-- Company Details -->
      <div class="px-4 md:px-0">
        <BasicFormTitle>
          Company Details
        </BasicFormTitle>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6 px-4 md:px-0">
        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">Company Name *</label>
          <TextInput
            type="text"
            v-model="companyName"
            placeholder="Enter Company Name"
          />
        </div>

        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">Registration Country *</label>
          <InputSelect
            :list="countries"
            v-model="registrationCountry"
          >
            <template #selected="{ item }">
              <div class="flex items-center gap-3">
                <Icon v-if="item" :icon="'flagpack:' + item.code.toLowerCase()" width="32" height="24" />
                <span v-if="item">{{ item.name }}</span>
              </div>
            </template>
            <template #item="{ item }">
              <Icon :icon="'flagpack:' + item.code.toLowerCase()" width="32" height="24" />
              <span class="ml-3 block truncate">{{ item.name }}</span>
            </template>
          </InputSelect>
        </div>

        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">Company Legal Form *</label>
          <TextInput
            type="text"
            v-model="companyLegalForm"
            placeholder="Enter Company Legal Form"
          />
        </div>

        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">Registration Number *</label>
          <TextInput
            type="text"
            v-model="registrationNumber"
            placeholder="Enter Registration Number"
          />
        </div>

        <div class="flex flex-col gap-y-2 md:col-span-2">
          <label class="text-sm font-medium text-gray-700">Registration Date *</label>
          <TextInput
            type="date"
            dateFormat="DD/MM/YYYY"
            v-model="registrationDate"
            placeholder="DD/MM/YYYY"
          />
        </div>
      </div>

      <!-- Tax Information -->
      <div class="px-4 md:px-0">
        <BasicFormTitle>
          Tax Information
        </BasicFormTitle>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6 px-4 md:px-0">
        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">Country of Tax Residence *</label>
          <InputSelect
            :list="countries"
            v-model="taxCountry"
          >
            <template #selected="{ item }">
              <div class="flex items-center gap-3">
                <Icon v-if="item" :icon="'flagpack:' + item.code.toLowerCase()" width="32" height="24" />
                <span v-if="item">{{ item.name }}</span>
              </div>
            </template>
            <template #item="{ item }">
              <Icon :icon="'flagpack:' + item.code.toLowerCase()" width="32" height="24" />
              <span class="ml-3 block truncate">{{ item.name }}</span>
            </template>
          </InputSelect>
        </div>

        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">Tax Number *</label>
          <TextInput
            type="text"
            v-model="taxNumber"
            placeholder="Enter Tax Number"
          />
        </div>
      </div>

      <!-- Contact Information -->
      <div class="px-4 md:px-0">
        <BasicFormTitle>
          Contact Information
        </BasicFormTitle>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6 px-4 md:px-0">
        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">Website Address *</label>
          <TextInput
            type="text"
            v-model="websiteAddress"
            placeholder="Enter Website Address"
          />
        </div>

        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">Phone Number *</label>
          <TextInput
            type="text"
            v-model="phoneNumber"
            placeholder="+371 20304050"
          />
        </div>
      </div>

      <!-- Legal Address Information -->
      <div class="px-4 md:px-0">
        <BasicFormTitle>
          Legal Address Information
        </BasicFormTitle>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6 px-4 md:px-0">
        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">Address Line *</label>
          <TextInput
            type="text"
            v-model="legalAddressLine"
            placeholder="Enter Address Line"
          />
        </div>

        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">City *</label>
          <TextInput
            type="text"
            v-model="legalCity"
            placeholder="Enter City"
          />
        </div>

        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">Country *</label>
          <InputSelect
            :list="countries"
            v-model="legalCountry"
          >
            <template #selected="{ item }">
              <div class="flex items-center gap-3">
                <Icon v-if="item" :icon="'flagpack:' + item.code.toLowerCase()" width="32" height="24" />
                <span v-if="item">{{ item.name }}</span>
              </div>
            </template>
            <template #item="{ item }">
              <Icon :icon="'flagpack:' + item.code.toLowerCase()" width="32" height="24" />
              <span class="ml-3 block truncate">{{ item.name }}</span>
            </template>
          </InputSelect>
        </div>

        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">Postcode/Zip Code *</label>
          <TextInput
            type="text"
            v-model="legalPostcode"
            placeholder="Enter Postcode/Zip Code"
          />
        </div>
      </div>

      <!-- Actual Address Information -->
      <div class="px-4 md:px-0">
        <BasicFormTitle>
          Actual Address Information
        </BasicFormTitle>
      </div>

      <div class="px-4 md:px-0">
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="actualAddressApplicable"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Actual address, if applicable</span>
        </label>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6 px-4 md:px-0">
        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">Address Line</label>
          <TextInput
            type="text"
            v-model="actualAddressLine"
            placeholder="Enter Address Line"
            :disabled="!actualAddressApplicable"
          />
        </div>

        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">City</label>
          <TextInput
            type="text"
            v-model="actualCity"
            placeholder="Enter City"
            :disabled="!actualAddressApplicable"
          />
        </div>

        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">Country</label>
          <InputSelect
            :list="countries"
            v-model="actualCountry"
            :disabled="!actualAddressApplicable"
          >
            <template #selected="{ item }">
              <div class="flex items-center gap-3">
                <Icon v-if="item" :icon="'flagpack:' + item.code.toLowerCase()" width="32" height="24" />
                <span v-if="item">{{ item.name }}</span>
              </div>
            </template>
            <template #item="{ item }">
              <Icon :icon="'flagpack:' + item.code.toLowerCase()" width="32" height="24" />
              <span class="ml-3 block truncate">{{ item.name }}</span>
            </template>
          </InputSelect>
        </div>

        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">Postcode/Zip Code</label>
          <TextInput
            type="text"
            v-model="actualPostcode"
            placeholder="Enter Postcode/Zip Code"
            :disabled="!actualAddressApplicable"
          />
        </div>
      </div>

      <!-- NACE Code and License -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6 px-4 md:px-0">
        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">NACE Code for Main *</label>
          <TextInput
            type="text"
            v-model="naceCode"
            placeholder="Enter NACA Code for Main"
          />
        </div>

        <div class="flex items-end pb-2">
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              v-model="requiresLicense"
              class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="text-sm text-gray-700">Does your business require a license?</span>
          </label>
        </div>
      </div>

      <!-- Business Activities Description -->
      <div class="px-4 md:px-0">
        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">Detailed Description of Business Activities *</label>
          <textarea
            v-model="businessActivitiesDescription"
            placeholder="Enter Detailed Description of Business Activities"
            rows="4"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          ></textarea>
        </div>
      </div>

      <div v-if="isLoading" class="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400 px-4 md:px-0">
        <div class="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent"></div>
        <span class="text-sm">Submitting...</span>
      </div>
    </div>
  </CardLayout>
</template>

<style scoped lang="scss">
</style>
